import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { capitalize, Grid, Typography } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { CriteriaOption } from 'commons/components/SearchRelated';
import { useUpdateQueryParams } from 'commons/components/SearchRelated/SearchWithCriteria/hooks';
import { InventoryAdjustmentSearchCriteriaEnum } from 'commons/enums';
import { getInventoryAdjustmentListAction } from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';

import { ListFilterCard, ListPage } from '../commons/components';

import InventoryAdjustmentTable from './components/InventoryAdjustmentTable.component';

const InventoryAdjustmentListPage: React.FC = () => {
  const { queryParams, updateQueryParams } = useUpdateQueryParams();

  const { inventoryAdjustmentList } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  const updateInventoryAdjustmentListSearchDebounce = useDebouncedCallback(
    (_searchParams, _location, _dispatch) => {
      _dispatch(
        getInventoryAdjustmentListAction({
          searchParams: _searchParams,
          locationId: _location,
        }),
      );
    },
    200,
    { maxWait: 1000 },
  );

  const searchCriteria: CriteriaOption[] = [
    {
      name: 'Document Number',
      id: 'document_number',
      isArray: true,
      parameterMapping: 'document_numbers',
    },
    {
      name: 'Created By',
      id: 'created_by',
      isArray: true,
      parameterMapping: 'created_bys',
    },
    {
      name: 'SKU Code',
      id: 'sku_code',
      isArray: true,
      parameterMapping: 'sku_codes',
    },
  ];

  const inventoryAdjustmentStatusArrayText: {
    key: InventoryAdjustmentSearchCriteriaEnum;
    text: string;
  }[] = [
    ...Object.values(InventoryAdjustmentSearchCriteriaEnum).map((value) => ({
      key: value,
      text: capitalize(value.replace('-', ' ')),
    })),
  ];

  const [statusFilter, setStatusFilter] =
    useState<InventoryAdjustmentSearchCriteriaEnum | null>(null);

  const handleStatusFilter = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    value: InventoryAdjustmentSearchCriteriaEnum,
  ): void => {
    setStatusFilter(value);
    updateQueryParams({ status: value }, { clearQueryParams: ['page'] });
  };

  return (
    <ListPage
      updateListSearchDebounce={updateInventoryAdjustmentListSearchDebounce}
    >
      <ListFilterCard
        statusArrayText={inventoryAdjustmentStatusArrayText}
        statusFilter={statusFilter}
        handleStatusFilter={handleStatusFilter}
        searchCriteria={searchCriteria}
      />
      {Object.keys(queryParams).includes('status') ? (
        <InventoryAdjustmentTable
          data={inventoryAdjustmentList}
          statusFilter={statusFilter}
        />
      ) : (
        <Grid container justifyContent="center">
          <Typography>Please select a status</Typography>
        </Grid>
      )}
    </ListPage>
  );
};

export default InventoryAdjustmentListPage;
